import { isNil, isNotNil } from '@wistia/type-guards';
import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';
import { styled, css } from 'styled-components';
import { ScreenReaderOnly } from '../../../components/ScreenReaderOnly';
import { FormControlLabelDescription } from './FormControlLabelDescription';

const disabledStyle = css`
  cursor: not-allowed;
  color: var(--wui-color-text-disabled);
`;

const StyledLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

type StyledFormControlLabelProps = {
  $disabled: boolean;
  $align: 'left' | 'right';
};

const StyledFormControlLabel = styled.label<StyledFormControlLabelProps>`
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: var(--wui-space-02);
  width: 100%;
  color: var(--wui-color-tex);
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-label-3-weight);
  line-height: var(--wui-typography-body-3-line-height);
  justify-content: ${({ $align }) => ($align === 'left' ? 'flex-start' : 'space-between')};

  ${({ $disabled }) => $disabled && disabledStyle}
`;

export type FormControlLabelProps = ComponentPropsWithoutRef<'label'> & {
  align?: 'left' | 'right';

  /**
   * The label for the form control
   */
  label?: ReactNode;
  /**
   * Whether the label is for a disabled form control
   */
  disabled?: boolean;
  /**
   * A slot to pass in a control element (eg. Radio, Checkbox or Switch)
   */
  controlSlot?: ReactNode;
  /**
   * The description for the form control
   */
  description?: ReactNode;
  /**
   * The id for the associated form control
   */
  htmlFor?: string;
  /**
   * Whether to visually hide the label (it will still be available to screen readers)
   */
  hideLabel?: boolean;
};

/**
 * FormControlLabel is used to give a label to a form control (eg. Radio, Checkbox or Switch)
 */
export const FormControlLabel = ({
  align = 'left',
  label,
  description,
  disabled = false,
  controlSlot,
  htmlFor,
  hideLabel = false,
  ...props
}: FormControlLabelProps): JSX.Element | null => {
  if (isNil(label)) {
    return null;
  }

  const labelContent = hideLabel ? (
    <ScreenReaderOnly>{label}</ScreenReaderOnly>
  ) : (
    <StyledLabelWrapper>
      {label}
      <FormControlLabelDescription>{description}</FormControlLabelDescription>
    </StyledLabelWrapper>
  );

  const slot = isNotNil(controlSlot) ? controlSlot : null;

  return (
    <StyledFormControlLabel
      $align={align}
      $disabled={disabled}
      htmlFor={htmlFor}
      {...props}
    >
      {align === 'left' ? slot : labelContent}
      {align === 'left' ? labelContent : slot}
    </StyledFormControlLabel>
  );
};

FormControlLabel.displayName = 'FormControlLabel';
